// 스트리밍 p95 노름 추적기 — 오프라인 뷰어의 per-clip p95(_visemeNorms)를
// 실시간 청크 스트림용으로 대체한다. 다운믹스의 정규화 기준값(jaw/round/spread/
// close/totMouth)을 최근 창(기본 10초)의 p95 로 추정하고, 세션 초반에는
// prior 와 블렌드해 첫 청크부터 그럴듯한 값이 나오게 한다.

/** 입 채널 총합(발화 게이트용) — ARKit 22~48 전체 + tongueOut(51). 49/50(noseSneer) 제외 */
export const MOUTH_SUM_CH: readonly number[] = [
  22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
  41, 42, 43, 44, 45, 46, 47, 48, 51,
];

export interface NormPriors {
  jaw: number;      // p95(ch24 jawOpen)
  round: number;    // p95(max(ch31 funnel, ch37 pucker))
  spread: number;   // p95((ch45+ch46)/2 mouthStretch)
  close: number;    // p95(ch26 mouthClose)
  totMouth: number; // p95(Σ MOUTH_SUM_CH)
}

const NORM_KEYS: readonly (keyof NormPriors)[] = ['jaw', 'round', 'spread', 'close', 'totMouth'];

/** 뷰어 실측 코멘트 기반 워밍 스타트 값 (totMouth 는 낮게 — 첫 청크 speech 게이트가 잘 열리도록) */
export const DEFAULT_NORM_PRIORS: NormPriors = {
  jaw: 0.25,
  round: 0.35,
  spread: 0.25,
  close: 0.22,
  totMouth: 1.5,
};

export class StreamingNormTracker {
  private readonly priors: NormPriors;
  private readonly windowFrames: number;
  private readonly warmupFrames: number;
  private readonly buffers: Record<keyof NormPriors, Float32Array>;
  private cursor = 0;
  private count = 0;

  constructor(priors: NormPriors, windowFrames = 300 /* 10초@30fps */, warmupFrames = 60 /* 2초 */) {
    this.priors = { ...priors };
    this.windowFrames = windowFrames;
    this.warmupFrames = warmupFrames;
    this.buffers = {
      jaw: new Float32Array(windowFrames),
      round: new Float32Array(windowFrames),
      spread: new Float32Array(windowFrames),
      close: new Float32Array(windowFrames),
      totMouth: new Float32Array(windowFrames),
    };
  }

  /** stage-A(크리스프+립스무딩 후) 프레임 하나를 통계에 반영 */
  pushFrame(fb: ArrayLike<number>): void {
    let tot = 0;
    for (let i = 0; i < MOUTH_SUM_CH.length; i++) tot += fb[MOUTH_SUM_CH[i]] || 0;
    this.buffers.jaw[this.cursor] = fb[24] || 0;
    this.buffers.round[this.cursor] = Math.max(fb[31] || 0, fb[37] || 0);
    this.buffers.spread[this.cursor] = ((fb[45] || 0) + (fb[46] || 0)) / 2;
    this.buffers.close[this.cursor] = fb[26] || 0;
    this.buffers.totMouth[this.cursor] = tot;
    this.cursor = (this.cursor + 1) % this.windowFrames;
    this.count++;
  }

  /** 현재 유효 노름 스냅샷 — 청크당 1회 호출해 청크 내에서 상수로 사용 */
  snapshot(): NormPriors {
    const valid = Math.min(this.count, this.windowFrames);
    const w = Math.min(1, this.count / this.warmupFrames);
    const result = { ...this.priors };
    if (valid > 0) {
      for (const key of NORM_KEYS) {
        const region = this.buffers[key].slice(0, valid);
        region.sort();
        const measured = region[Math.floor(valid * 0.95)] ?? region[valid - 1];
        result[key] = this.priors[key] * (1 - w) + measured * w;
      }
    }
    for (const key of NORM_KEYS) result[key] = Math.max(0.05, result[key]);
    return result;
  }

  reset(): void {
    this.cursor = 0;
    this.count = 0;
  }
}
